/** Post endpoint calls — CRUD for regular posts, events, and offers; link preview; boosts list; replies. */
import type { Post, EventPost, OfferPost, HelpPost, ActionPost, ActionRsvp, ActionParticipation, Reply, PaginatedResponse, ClapToggleResponse, PostClapsResponse } from '@olimhub/shared'
import type { Language, OfferType, RsvpVisibility } from '@olimhub/shared'
import { apiClient } from '../client'

export interface LinkPreviewResult {
  url: string
  title: string | null
  description: string | null
  image: string | null
  type: 'youtube' | 'vimeo' | 'link'
  videoId: string | null
}

export type AnyPost = Post | EventPost | OfferPost | HelpPost | ActionPost

// Group posting (Phase B1) — two mutually exclusive modes:
// postAsGroupSlug: author as that group's mirror account (post_as_group
//   capability required); showInLocalFeed only applies with this mode
//   (defaults to false server-side when omitted).
// groupTagSlug: post as yourself, tagged into the group's feed (active
//   membership required); always shows in Local/Following as normal.
export type CreatePostInput =
  | { type: 'post'; content: string; language: Language; imageUrl?: string; postAsGroupSlug?: string; groupTagSlug?: string; showInLocalFeed?: boolean }
  | {
      type: 'event'
      content: string
      language: Language
      imageUrl?: string
      startsAt: string
      endsAt?: string
      placeText?: string
      // Client-resolved coordinates (Google Places pick) — when present,
      // the server trusts them directly and skips the async geocode job.
      latitude?: number
      longitude?: number
      categoryId?: string | null
      postAsGroupSlug?: string
      groupTagSlug?: string
      showInLocalFeed?: boolean
    }
  | {
      type: 'offer'
      content: string
      language: Language
      imageUrl?: string
      offerType: OfferType
      categoryId?: string | null
      postAsGroupSlug?: string
      groupTagSlug?: string
      showInLocalFeed?: boolean
    }
  | {
      type: 'help'
      content: string
      language: Language
      categoryId?: string | null
      isUrgent?: boolean
      postAsGroupSlug?: string
      groupTagSlug?: string
      showInLocalFeed?: boolean
    }
  | {
      type: 'action'
      content: string
      language: Language
      imageUrl?: string
      title: string
      startsAt: string
      locationText?: string
      // Client-resolved coordinates (Google Places pick) — when present,
      // the server trusts them directly and skips the async geocode job.
      latitude?: number
      longitude?: number
      needsText?: string
      rsvpVisibility: RsvpVisibility
      categoryId?: string | null
      // An Action always belongs to a group — one of these is required.
      postAsGroupSlug?: string
      groupTagSlug?: string
      showInLocalFeed?: boolean
    }

export interface UpdatePostInput {
  content?: string
  language?: Language
  imageUrl?: string | null
  // event fields
  startsAt?: string
  endsAt?: string | null
  placeText?: string | null
  // Client-resolved coordinates (Google Places pick) — shared by placeText
  // (event) and locationText (action), like the fields themselves.
  latitude?: number
  longitude?: number
  // offer fields
  offerType?: OfferType
  // Admin-curated category — event/offer/help/action only, single field
  // regardless of the post's type (validated against it server-side).
  categoryId?: string | null
  isUrgent?: boolean
  // action fields
  title?: string
  locationText?: string | null
  needsText?: string | null
  rsvpVisibility?: RsvpVisibility
  showInLocalFeed?: boolean
}

export const postsApi = {
  create: (body: CreatePostInput) =>
    apiClient.post<AnyPost>('/posts', body).then((r) => r.data),

  getById: (id: string) =>
    apiClient.get<AnyPost>(`/posts/${id}`).then((r) => r.data),

  update: (id: string, body: UpdatePostInput) =>
    apiClient.put<AnyPost>(`/posts/${id}`, body).then((r) => r.data),

  delete: (id: string) =>
    apiClient.delete(`/posts/${id}`).then((r) => r.data),

  report: (id: string, reason: string) =>
    apiClient.post(`/posts/${id}/report`, { reason }).then((r) => r.data),

  getBoosts: (id: string, cursor?: string) =>
    apiClient
      .get<PaginatedResponse<{ id: string }>>(`/posts/${id}/boosts`, { params: { cursor } })
      .then((r) => r.data),

  getLinkPreview: (url: string) =>
    apiClient.get<LinkPreviewResult>('/posts/link-preview', { params: { url } }).then((r) => r.data),

  getReplies: (id: string, cursor?: string) =>
    apiClient
      .get<PaginatedResponse<Reply>>(`/posts/${id}/replies`, { params: cursor ? { cursor } : {} })
      .then((r) => r.data),

  createReply: (id: string, body: { content: string; language: Language }) =>
    apiClient.post<Reply>(`/posts/${id}/replies`, body).then((r) => r.data),

  markResolved: (id: string, resolvedBy?: string) =>
    apiClient.patch<HelpPost>(`/posts/${id}/resolve`, { resolvedBy }).then((r) => r.data),

  clap: (id: string) =>
    apiClient.post<ClapToggleResponse>(`/posts/${id}/clap`).then((r) => r.data),

  getClaps: (id: string) =>
    apiClient.get<PostClapsResponse>(`/posts/${id}/claps`).then((r) => r.data),
}

export const actionsApi = {
  rsvp: (postId: string) =>
    apiClient.post(`/actions/${postId}/rsvp`).then((r) => r.data),

  cancelRsvp: (postId: string) =>
    apiClient.delete(`/actions/${postId}/rsvp`).then((r) => r.data),

  getRsvps: (postId: string) =>
    apiClient.get<ActionRsvp[]>(`/actions/${postId}/rsvps`).then((r) => r.data),

  getParticipations: (postId: string) =>
    apiClient.get<ActionParticipation[]>(`/actions/${postId}/participations`).then((r) => r.data),

  attest: (postId: string, accountId: string) =>
    apiClient.post(`/actions/${postId}/attest`, { accountId }).then((r) => r.data),

  unattest: (postId: string, accountId: string) =>
    apiClient.delete(`/actions/${postId}/attest/${accountId}`).then((r) => r.data),
}
